<template>
  <div class="space-y-8">
    <!-- Lease terms -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <CurrencyEuroIcon class="h-5 w-5 text-indigo-500" />
        <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Bail") }}</h4>
      </div>
      <div class="border border-gray-200 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-3">
          <ExtractionInput v-model="data.price" type="number" :label="$gettext('Loyer mensuel')" required :error="!data.price" />
          <ExtractionInput v-model="data.leaseTypeId" type="select" :label="$gettext('Type de bail')" :options="leaseTypeOptions" required :error="!data.leaseTypeId" />
          <ExtractionInput v-model="data.signatureDate" type="date" :label="$gettext('Date de signature')" required :error="!data.signatureDate" />
          <ExtractionInput v-model="data.endDate" type="date" :label="$gettext('Date de fin')" required :error="!data.endDate || !isEndDateAfterSignature" :defaulted="isDefaulted('endDate')" />
        </div>
        <p v-if="!isEndDateAfterSignature" class="mt-2 text-xs text-red-600">
          {{ $gettext("La date de fin doit être postérieure à la date de signature.") }}
        </p>
      </div>
    </div>

    <!-- Tenants -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <UserGroupIcon class="h-5 w-5 text-indigo-500" />
          <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Locataires") }}</h4>
        </div>
        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
          {{ data.tenants.length }}
        </span>
      </div>

      <div v-for="(tenant, index) in data.tenants" :key="index" class="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-2 border-l-indigo-200">

        <!-- ===== Company tenant card ===== -->
        <template v-if="tenant.type === 'company'">
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedTenants[index] = !expandedTenants[index]"
            >
              <div class="flex items-center gap-3">
                <BriefcaseIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Société") }} {{ index + 1 }}
                  <span v-if="tenant.companyName" class="text-gray-500">— {{ tenant.companyName }}</span>
                </span>
                <span v-if="tenant.matchedCompanyId" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existante") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créée") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedTenants[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeTenant(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedTenants[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="tenant.companyName" :label="$gettext('Dénomination sociale')" required :error="!tenant.matchedCompanyId && !tenant.companyName?.trim()" />
              <ExtractionInput v-model="tenant.companyNumber" :label="$gettext('Numéro d\'entreprise')" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="tenant.companyAddress.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!tenant.matchedCompanyId && !tenant.companyAddress.street?.trim()" @address-selected="(a) => { tenant.companyAddress.city = a.city; tenant.companyAddress.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="tenant.companyAddress.zipcode" :placeholder="$gettext('Code postal')" required :error="!tenant.matchedCompanyId && !tenant.companyAddress.zipcode?.trim()" />
                  <ExtractionInput v-model="tenant.companyAddress.city" :placeholder="$gettext('Ville')" required :error="!tenant.matchedCompanyId && !tenant.companyAddress.city?.trim()" />
                </div>
              </div>
              <!-- Required fields dropdowns (only for new companies) -->
              <template v-if="!tenant.matchedCompanyId">
                <ExtractionInput v-model="tenant.companyTypeId" type="select" :label="$gettext('Forme juridique')" required :error="!tenant.companyTypeId" :options="companyTypeOptions" />
                <ExtractionInput v-model="tenant.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!tenant.industrySectorId" :options="industrySectorOptions" />
              </template>
            </div>

            <!-- Signatory sub-section -->
            <div v-if="tenant.representative" class="mt-4 pt-4 border-t border-gray-200">
              <h5 class="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">{{ $gettext("Signataire") }}</h5>
              <div class="grid grid-cols-2 gap-3">
                <ExtractionInput v-model="tenant.representative.lastName" :label="$gettext('Nom')" required :error="!tenant.representative.matchedPhysicalPersonId && !tenant.representative.lastName?.trim()" />
                <ExtractionInput v-model="tenant.representative.firstName" :label="$gettext('Prénom')" required :error="!tenant.representative.matchedPhysicalPersonId && !tenant.representative.firstName?.trim()" />
                <ExtractionInput v-model="tenant.representative.role" :label="$gettext('Fonction')" />
                <ExtractionInput v-model="tenant.representative.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!tenant.representative.matchedPhysicalPersonId && (tenant.representative.firstName?.trim() || tenant.representative.lastName?.trim()) && !tenant.representative.birthdate" />
                <ExtractionInput v-model="tenant.representative.birthCity" :label="$gettext('Lieu de naissance')" />
                <ExtractionInput v-model="tenant.representative.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!tenant.representative.matchedPhysicalPersonId && (tenant.representative.firstName?.trim() || tenant.representative.lastName?.trim()) && !tenant.representative.nationalityCountryId" />
                <div class="col-span-2">
                  <ExtractionAddressAutocomplete v-model="tenant.representative.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!tenant.representative.matchedPhysicalPersonId && !tenant.representative.address.street?.trim()" @address-selected="(a) => { tenant.representative.address.city = a.city; tenant.representative.address.zipcode = a.zipcode; }" />
                  <div class="grid grid-cols-2 gap-2 mt-1">
                    <ExtractionInput v-model="tenant.representative.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!tenant.representative.matchedPhysicalPersonId && !tenant.representative.address.zipcode?.trim()" />
                    <ExtractionInput v-model="tenant.representative.address.city" :placeholder="$gettext('Ville')" required :error="!tenant.representative.matchedPhysicalPersonId && !tenant.representative.address.city?.trim()" />
                  </div>
                </div>
                <!-- PEP questions for representative (only for new physical persons) -->
                <template v-if="!tenant.representative.matchedPhysicalPersonId">
                  <ExtractionInput v-model="tenant.representative.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="tenant.representative.isPoliticalyExposed === null" />
                  <ExtractionInput v-model="tenant.representative.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="tenant.representative.isCloseToPoliticalyExposed === null" />
                  <ExtractionInput v-model="tenant.representative.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!tenant.representative.identificationType" :options="identificationTypeOptions" />
                  <ExtractionInput v-model="tenant.representative.professionId" type="select" :label="$gettext('Profession')" required :error="!tenant.representative.professionId" :options="professionOptions" />
                  <ExtractionInput v-model="tenant.representative.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!tenant.representative.industrySectorId" :options="industrySectorOptions" />
                </template>
                <!-- ID document upload for representative -->
                <IdentityDocumentUpload
                  :person="tenant"
                  :person-index="index"
                  :is-extracting="extractingIdentityIndex === index"
                  :identity-error="identityErrorIndex === index ? identityError : null"
                  :label="$gettext('Documents d\'identité du signataire')"
                  @add-file="({ event }) => onIdentityFileAdd(event, index)"
                  @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                  @extract="() => onExtractIdentity(index)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ===== Physical person tenant card ===== -->
        <template v-else>
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedTenants[index] = !expandedTenants[index]"
            >
              <div class="flex items-center gap-3">
                <UserIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Locataire") }} {{ index + 1 }}
                  <span v-if="tenant.firstName || tenant.lastName" class="text-gray-500">— {{ [tenant.firstName, tenant.lastName].filter(Boolean).join(' ') }}</span>
                </span>
                <span v-if="tenant.matchConfidence === 'high'" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existant") }}
                </span>
                <span v-else-if="tenant.matchConfidence === 'medium'" class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  {{ $gettext("Correspondance partielle") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créé") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedTenants[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeTenant(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedTenants[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="tenant.lastName" :label="$gettext('Nom')" required :error="!tenant.matchedPhysicalPersonId && !tenant.lastName?.trim()" />
              <ExtractionInput v-model="tenant.firstName" :label="$gettext('Prénom')" required :error="!tenant.matchedPhysicalPersonId && !tenant.firstName?.trim()" />
              <ExtractionInput v-model="tenant.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!tenant.matchedPhysicalPersonId && (tenant.firstName?.trim() || tenant.lastName?.trim()) && !tenant.birthdate" />
              <ExtractionInput v-model="tenant.birthCity" :label="$gettext('Lieu de naissance')" />
              <ExtractionInput v-model="tenant.nationalNumber" :label="$gettext('Numéro national')" />
              <ExtractionInput v-model="tenant.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!tenant.matchedPhysicalPersonId && (tenant.firstName?.trim() || tenant.lastName?.trim()) && !tenant.nationalityCountryId" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="tenant.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!tenant.matchedPhysicalPersonId && !tenant.address.street?.trim()" @address-selected="(a) => { tenant.address.city = a.city; tenant.address.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="tenant.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!tenant.matchedPhysicalPersonId && !tenant.address.zipcode?.trim()" />
                  <ExtractionInput v-model="tenant.address.city" :placeholder="$gettext('Ville')" required :error="!tenant.matchedPhysicalPersonId && !tenant.address.city?.trim()" />
                </div>
              </div>
              <!-- Required fields (only for new physical persons) -->
              <template v-if="!tenant.matchedPhysicalPersonId">
                <ExtractionInput v-model="tenant.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="tenant.isPoliticalyExposed === null" />
                <ExtractionInput v-model="tenant.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="tenant.isCloseToPoliticalyExposed === null" />
                <ExtractionInput v-model="tenant.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!tenant.identificationType" :options="identificationTypeOptions" />
                <ExtractionInput v-model="tenant.professionId" type="select" :label="$gettext('Profession')" required :error="!tenant.professionId" :options="professionOptions" />
                <ExtractionInput v-model="tenant.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!tenant.industrySectorId" :options="industrySectorOptions" />
              </template>
              <!-- ID document upload -->
              <IdentityDocumentUpload
                :person="tenant"
                :person-index="index"
                :is-extracting="extractingIdentityIndex === index"
                :identity-error="identityErrorIndex === index ? identityError : null"
                @add-file="({ event }) => onIdentityFileAdd(event, index)"
                @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                @extract="() => onExtractIdentity(index)"
              />
            </div>
          </div>
        </template>

      </div>
      <p v-if="data.tenants.length === 0" class="text-sm text-gray-500">
        {{ $gettext("Aucun locataire n'a été détecté dans le document. Vous pourrez les ajouter dans le formulaire.") }}
      </p>
      <p v-if="!isValid" class="mt-2 text-xs text-red-600">
        {{ $gettext("Veuillez remplir tous les champs obligatoires (*) avant de confirmer.") }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, toRaw } from "vue";
import { CurrencyEuroIcon, UserGroupIcon, BriefcaseIcon, UserIcon, ChevronDownIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { useIndustrySectorStore } from "@/stores/industrySector";
import { useProfessionStore } from "@/stores/profession";
import { useCompanyTypeStore } from "@/stores/companyType";
import { useCountryStore } from "@/stores/country";
import { useLeaseTypeStore } from "@/stores/leaseType";
import ExtractionInput from "@/components/ui/ExtractionInput.vue";
import ExtractionAddressAutocomplete from "@/components/ui/ExtractionAddressAutocomplete.vue";
import IdentityDocumentUpload from "@/components/ui/IdentityDocumentUpload.vue";

const props = defineProps({
  extractionData: { type: Object, required: true },
  // Fields the document did not contain, filled from the organization's settings.
  defaultedFields: { type: Array, default: () => [] }
});

const isDefaulted = (field) => props.defaultedFields.includes(field);

const emit = defineEmits(["extract-identity"]);

const industrySectorStore = useIndustrySectorStore();
const professionStore = useProfessionStore();
const companyTypeStore = useCompanyTypeStore();
const countryStore = useCountryStore();
const leaseTypeStore = useLeaseTypeStore();

// Fetch Belgian company types when countries are loaded
watch(() => countryStore.sortedCountries, (countries) => {
  if (!countries?.length) return;
  const belgium = countries.find(c => c.attributes.code === "BE");
  if (belgium) companyTypeStore.fetchCompanyTypes(belgium.id);
}, { immediate: true });

const industrySectorOptions = computed(() =>
  industrySectorStore.industrySectors.map(is => ({ value: is.id, label: is.attributes.name }))
);
const professionOptions = computed(() =>
  professionStore.professions.map(p => ({ value: p.id, label: p.attributes.name }))
);
const companyTypeOptions = computed(() =>
  companyTypeStore.companyTypes.map(ct => ({ value: ct.id, label: ct.attributes.name }))
);
const nationalityCountryOptions = computed(() =>
  countryStore.sortedCountries.map(c => ({ value: c.id, label: c.attributes.demonym || c.attributes.name }))
);
const leaseTypeOptions = computed(() =>
  leaseTypeStore.sortedLeaseTypes.map(lt => ({ value: lt.id, label: lt.attributes.name }))
);
const identificationTypeOptions = [
  { value: "faceToFace", label: "Face à face" },
  { value: "remote", label: "A distance" },
  { value: "none", label: "Aucun" }
];

const data = reactive({
  price: props.extractionData.price || null,
  signatureDate: props.extractionData.signatureDate || "",
  endDate: props.extractionData.endDate || "",
  leaseTypeId: props.extractionData.matchedLeaseTypeId || null,
  tenants: (props.extractionData.tenants || []).map(t => ({
    ...t,
    type: t.type || "physicalPerson",
    identityFiles: [],
    identityExtracted: false,
    companyName: t.companyName || "",
    companyNumber: t.companyNumber || "",
    companyTypeId: t.companyTypeId || null,
    industrySectorId: t.industrySectorId || null,
    companyAddress: {
      street: t.companyAddress?.street || "",
      city: t.companyAddress?.city || "",
      zipcode: t.companyAddress?.zipcode || ""
    },
    representative: t.representative ? {
      ...t.representative,
      role: t.representative.role || "",
      birthCity: t.representative.birthCity || "",
      birthdate: t.representative.birthdate || "",
      isPoliticalyExposed: t.representative.isPoliticalyExposed ?? null,
      isCloseToPoliticalyExposed: t.representative.isCloseToPoliticalyExposed ?? null,
      identificationType: t.representative.identificationType || null,
      professionId: t.representative.professionId || null,
      nationalityCountryId: t.representative.nationalityCountryId || null,
      industrySectorId: t.representative.industrySectorId || null,
      address: {
        street: t.representative.address?.street || "",
        city: t.representative.address?.city || "",
        zipcode: t.representative.address?.zipcode || ""
      }
    } : null,
    isPoliticalyExposed: t.isPoliticalyExposed ?? null,
    isCloseToPoliticalyExposed: t.isCloseToPoliticalyExposed ?? null,
    identificationType: t.identificationType || null,
    professionId: t.professionId || null,
    nationalityCountryId: t.nationalityCountryId || null,
    birthCity: t.birthCity || "",
    birthdate: t.birthdate || "",
    nationalNumber: t.nationalNumber || "",
    address: { street: t.address?.street || "", city: t.address?.city || "", zipcode: t.address?.zipcode || "" }
  }))
});

const extractingIdentityIndex = ref(null);
const identityError = ref(null);
const identityErrorIndex = ref(null);

// Collapsible state — all expanded by default
const expandedTenants = ref(data.tenants.map(() => true));

const removeTenant = (index) => {
  data.tenants.splice(index, 1);
  expandedTenants.value.splice(index, 1);
};

const isEndDateAfterSignature = computed(() => {
  if (!data.endDate || !data.signatureDate) return true;
  return new Date(data.endDate) > new Date(data.signatureDate);
});

const isValid = computed(() => {
  if (!data.price) return false;
  if (!data.leaseTypeId) return false;
  if (!data.signatureDate) return false;
  if (!data.endDate) return false;
  if (!isEndDateAfterSignature.value) return false;

  return data.tenants.every(tenant => {
    if (tenant.type === "company") {
      if (!tenant.matchedCompanyId) {
        if (!tenant.companyName?.trim()) return false;
        if (!tenant.companyAddress.street?.trim()) return false;
        if (!tenant.companyAddress.zipcode?.trim()) return false;
        if (!tenant.companyAddress.city?.trim()) return false;
        if (!tenant.companyTypeId) return false;
        if (!tenant.industrySectorId) return false;
      }
      if (tenant.representative && !tenant.representative.matchedPhysicalPersonId) {
        // PhysicalPerson requires both names; a half-filled one would 400 on apply.
        if (!tenant.representative.firstName?.trim()) return false;
        if (!tenant.representative.lastName?.trim()) return false;
        if (!tenant.representative.birthdate) return false;
        if (!tenant.representative.nationalityCountryId) return false;
        if (!tenant.representative.address.street?.trim()) return false;
        if (!tenant.representative.address.zipcode?.trim()) return false;
        if (!tenant.representative.address.city?.trim()) return false;
        if (tenant.representative.isPoliticalyExposed === null) return false;
        if (tenant.representative.isCloseToPoliticalyExposed === null) return false;
        if (!tenant.representative.identificationType) return false;
        if (!tenant.representative.professionId) return false;
        if (!tenant.representative.industrySectorId) return false;
      }
      return true;
    } else {
      if (tenant.matchedPhysicalPersonId) return true;
      // PhysicalPerson requires both names; a half-filled one would 400 on apply,
      // and a fully empty one would be silently dropped. Use the trash button instead.
      if (!tenant.firstName?.trim()) return false;
      if (!tenant.lastName?.trim()) return false;
      if (!tenant.birthdate) return false;
      if (!tenant.nationalityCountryId) return false;
      if (!tenant.address.street?.trim()) return false;
      if (!tenant.address.zipcode?.trim()) return false;
      if (!tenant.address.city?.trim()) return false;
      if (tenant.isPoliticalyExposed === null) return false;
      if (tenant.isCloseToPoliticalyExposed === null) return false;
      if (!tenant.identificationType) return false;
      if (!tenant.professionId) return false;
      if (!tenant.industrySectorId) return false;
      return true;
    }
  });
});

const onIdentityFileAdd = (event, tenantIndex) => {
  const file = event.target.files[0];
  if (!file) return;
  event.target.value = "";

  const tenant = data.tenants[tenantIndex];
  if (!tenant || tenant.identityFiles.length >= 2) return;

  tenant.identityFiles.push(file);
  tenant.identityExtracted = false;
  identityError.value = null;
  identityErrorIndex.value = null;
};

const removeIdentityFile = (tenantIndex, fileIndex) => {
  const tenant = data.tenants[tenantIndex];
  if (!tenant) return;
  tenant.identityFiles.splice(fileIndex, 1);
  tenant.identityExtracted = false;
};

const onExtractIdentity = (tenantIndex) => {
  const tenant = data.tenants[tenantIndex];
  if (!tenant || tenant.identityFiles.length === 0) return;

  identityError.value = null;
  identityErrorIndex.value = null;
  extractingIdentityIndex.value = tenantIndex;

  emit("extract-identity", { files: tenant.identityFiles.map(f => toRaw(f)), tenantIndex });
};

const mergeIdentityData = (tenantIndex, identityData) => {
  extractingIdentityIndex.value = null;
  const tenant = data.tenants[tenantIndex];
  if (!tenant) return;

  tenant.identityExtracted = true;

  const target = tenant.type === "company" && tenant.representative ? tenant.representative : tenant;

  if (identityData.lastName) target.lastName = identityData.lastName;
  if (identityData.firstName) target.firstName = identityData.firstName;
  if (identityData.birthdate) target.birthdate = identityData.birthdate;
  if (identityData.birthCity) target.birthCity = identityData.birthCity;
  if (identityData.nationalNumber) target.nationalNumber = identityData.nationalNumber;
  if (identityData.nationalityCountryId) target.nationalityCountryId = identityData.nationalityCountryId;
  if (identityData.address?.street) target.address.street = identityData.address.street;
  if (identityData.address?.city) target.address.city = identityData.address.city;
  if (identityData.address?.zipcode) target.address.zipcode = identityData.address.zipcode;

  if (identityData.matchedPhysicalPersonId) {
    target.matchedPhysicalPersonId = identityData.matchedPhysicalPersonId;
    target.matchedPhysicalPersonName = identityData.matchedPhysicalPersonName;
    target.matchConfidence = identityData.matchConfidence;
  }
};

const onIdentityError = (tenantIndex, message) => {
  extractingIdentityIndex.value = null;
  identityError.value = message;
  identityErrorIndex.value = tenantIndex;
};

const getReviewedData = () => {
  return {
    price: data.price,
    signatureDate: data.signatureDate,
    endDate: data.endDate,
    leaseTypeId: data.leaseTypeId,
    tenants: data.tenants
  };
};

const getIdentityFilesPerTenant = () => {
  return data.tenants.map((t, idx) => ({
    tenantIndex: idx,
    tenantType: t.type,
    files: (t.identityFiles || []).map(f => toRaw(f))
  }));
};

defineExpose({ getReviewedData, mergeIdentityData, onIdentityError, isValid, getIdentityFilesPerTenant });
</script>
